import { formatAnyPhoneNumber } from "@/lib/phone"
import { ApiError, CallbackPhoneNumber } from "@/lib/vibrato-api"
import React, { useContext, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { VibratoClientContext } from "./contexts"
import VerifyPhoneNumberModal from "./VerifyPhoneNumberModal"
import Notifications, { Notif } from "./Notifications"
import { Phone, Plus, Check, HelpCircle } from "iconoir-react"

const CallbackNumberList: React.FC = () => {
  const [callbackPhoneNumbers, setCallbackPhoneNumbers] = useState<CallbackPhoneNumber[]>([])
  const vibratoClient = useContext(VibratoClientContext)
  const [showConfirmPhoneNumberModal, setShowConfirmPhoneNumberModal] = useState(false)
  const [notifications, setNotifications] = useState<Notif[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    document.title = "Vibrato - Callback Numbers"
  }, [])

  // Check for URL parameters to auto-open modal
  useEffect(() => {
    const shouldOpenModal = searchParams.get("add") === "true" || searchParams.get("modal") === "add"
    if (shouldOpenModal) {
      setShowConfirmPhoneNumberModal(true)
      // Clean up the URL parameter
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete("add")
      newSearchParams.delete("modal")
      setSearchParams(newSearchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    const fetchCallbackPhoneNumbers = async () => {
      if (vibratoClient) {
        try {
          setIsLoading(true)
          const callback_phone_numbers = await vibratoClient.getCallbackPhoneNumbers()
          setCallbackPhoneNumbers(callback_phone_numbers)
        } catch (error) {
          console.error("Failed to fetch callback phone numbers:", error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    fetchCallbackPhoneNumbers()
  }, [vibratoClient])

  const removeCallbackNumber = async (uuid: string) => {
    if (vibratoClient) {
      try {
        await vibratoClient.deleteCallbackPhoneNumber(uuid)
        setCallbackPhoneNumbers(callbackPhoneNumbers.filter((number) => number.uuid !== uuid))
      } catch (error) {
        console.error("Failed to remove callback number:", error)
        if (error instanceof ApiError && error.data) {
          const errorMessage = Array.isArray(error.data) ? error.data.join(", ") : "Failed to remove callback number"
          setNotifications([
            {
              id: Date.now().toString(),
              type: "error",
              header: "Error",
              message: errorMessage
            }
          ])
        }
      }
    }
  }

  const deleteNotification = (id: string) => {
    setNotifications(notifications.filter((notif) => notif.id !== id))
  }

  return (
    <div className="h-full w-full p-6 md:p-8 flex flex-col gap-6">
      <Notifications notifs={notifications} deleteNotif={deleteNotification} />
      {showConfirmPhoneNumberModal && (
        <VerifyPhoneNumberModal
          show={showConfirmPhoneNumberModal}
          setShow={setShowConfirmPhoneNumberModal}
          mode="callback"
          onClose={() => setShowConfirmPhoneNumberModal(false)}
          onPhoneNumberVerified={async () => {
            setShowConfirmPhoneNumberModal(false)
            // Refresh the list after successful confirmation
            vibratoClient.getCallbackPhoneNumbers().then(setCallbackPhoneNumbers)
          }}
        />
      )}

      <div className="flex justify-between items-center flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-xl">
            <Phone width="27" className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Callback Numbers</h1>
            <p className="text-gray-600">Manage your callback numbers</p>
          </div>
        </div>

        <button
          onClick={() => setShowConfirmPhoneNumberModal(true)}
          className="px-4 sm:px-6 py-3 bg-linear-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 min-w-[180px]"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Add Callback Number</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      <nav className="flex border-b border-gray-200 dark:border-gray-700">
        <ul className="flex flex-wrap -mb-px text-sm font-medium text-center text-gray-500 dark:text-gray-400">
          <li className="mr-2">
            <Link
              to="/phone-numbers"
              className="inline-flex items-center justify-center p-4 border-b-2 rounded-t-lg border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
            >
              Phone Numbers
            </Link>
          </li>
          <li className="mr-2">
            <Link
              to="/callback-numbers"
              className="inline-flex items-center justify-center p-4 border-b-2 rounded-t-lg text-blue-600 border-blue-600 active dark:text-blue-500 dark:border-blue-500"
            >
              Callback Numbers
            </Link>
          </li>
        </ul>
      </nav>

      <div className="bg-slate-50 p-4 md:p-6 rounded-xl border border-slate-200">
        <p className="text-slate-700">
          Callback numbers are phone numbers that you can use to receive incoming calls. You can add multiple callback
          numbers to your account and use them for different purposes.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64 bg-white rounded-lg shadow-md">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4" />
            <p className="text-gray-600">Loading callback numbers...</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col max-h-full bg-white shadow-md rounded-lg">
          {callbackPhoneNumbers.length > 0 ? (
            <div className="w-full">
              {/* Table for md and larger screens */}
              <div className="hidden md:block overflow-auto rounded-lg">
                <table className="max-h-full w-full text-sm text-left text-gray-500 dark:text-gray-400 divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Phone Number
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Verification Status
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {callbackPhoneNumbers.map((callbackPhoneNumber: CallbackPhoneNumber) => (
                      <tr key={callbackPhoneNumber.uuid} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-base font-medium text-gray-900">
                            {formatAnyPhoneNumber(callbackPhoneNumber.country_code, callbackPhoneNumber.phone_number)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {callbackPhoneNumber.verified_at ? (
                            <div className="flex items-center">
                              <Check className="w-5 h-5 text-green-500 mr-2" />
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Verified
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <HelpCircle className="w-5 h-5 text-amber-500 mr-2" />
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                Not Verified
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          <button
                            onClick={() => removeCallbackNumber(callbackPhoneNumber.uuid)}
                            className="p-2 text-red-600 hover:text-red-900 bg-red-50 rounded-lg transition-colors"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Card layout for mobile devices */}
              <div className="md:hidden rounded-lg overflow-hidden">
                {/* Add the table header for mobile view */}
                <div className="bg-gray-50 px-4 py-3 border-b border-t border-gray-200">
                  <div className="flex text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="w-2/5">Phone Number</div>
                  </div>
                </div>
                {callbackPhoneNumbers.map((callbackPhoneNumber: CallbackPhoneNumber) => (
                  <div
                    key={callbackPhoneNumber.uuid}
                    className="p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500 uppercase tracking-wider mb-1">Phone Number</span>
                        <div className="text-lg font-medium text-gray-900">
                          {formatAnyPhoneNumber(callbackPhoneNumber.country_code, callbackPhoneNumber.phone_number)}
                        </div>
                      </div>
                      {callbackPhoneNumber.verified_at ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Verified
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Not Verified
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {callbackPhoneNumber.verified_at ? (
                          <Check className="w-5 h-5 text-green-500 mr-2 shrink-0" />
                        ) : (
                          <HelpCircle className="w-5 h-5 text-amber-500 mr-2 shrink-0" />
                        )}
                        <div className="text-sm text-gray-500">
                          {callbackPhoneNumber.verified_at ? "Verified number" : "Needs verification"}
                        </div>
                      </div>
                      <button
                        onClick={() => removeCallbackNumber(callbackPhoneNumber.uuid)}
                        className="p-2 text-red-600 hover:text-red-900 bg-red-50 rounded-lg transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-fit p-6">
              <div className="flex flex-col items-center justify-center my-4">
                <div className="p-4 bg-purple-100 rounded-full mb-4">
                  <Phone className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No callback numbers yet</h3>
                <p className="text-gray-600 text-center max-w-md mb-6">
                  Add your first callback number to receive incoming calls from your purchased phone numbers.
                </p>
                <button
                  onClick={() => setShowConfirmPhoneNumberModal(true)}
                  className="px-6 py-3 bg-linear-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Callback Number
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CallbackNumberList
